import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  Package, 
  Truck, 
  Plus,
  MapPin,
  Apple,
  Coffee,
  Cake,
  Gift,
} from 'lucide-react'
import { useUser } from '../../contexts/UserContext'
import { getMyCreatedDonations, createDonation, uploadDonationPhoto, markDonationPickedUp } from '../../api/donations'
import DonationListingForm, { DonationListingData } from '../DonationListingForm'
import { getMyBusinessVerificationStatus } from '../../api/businessVerification'

import { Donation } from '../../api/donations'

const SupplierDashboard = () => {
  const { user, isAuthenticated } = useUser()
  const [myDeliveries, setMyDeliveries] = useState<Donation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showListingForm, setShowListingForm] = useState(false)
  const [isSubmittingListing, setIsSubmittingListing] = useState(false)
  const [actionDonationId, setActionDonationId] = useState<string | null>(null)
  const [businessVerification, setBusinessVerification] = useState<any>(null)

  useEffect(() => {
    const fetchMyDeliveries = async () => {
      // Only fetch if user is authenticated
      if (!isAuthenticated) {
        setIsLoading(false)
        return
      }

      try {
        const donations = await getMyCreatedDonations()
        setMyDeliveries(donations.slice(0, 5)) // Show first 5
      } catch (error) {
        console.error('Error fetching my deliveries:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchMyDeliveries()
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated || user?.userType !== 'SUPPLIER') return
    ;(async () => {
      try {
        const s = await getMyBusinessVerificationStatus()
        setBusinessVerification(s)
      } catch {
        setBusinessVerification(null)
      }
    })()
  }, [isAuthenticated, user?.userType])

  const handleCreateListing = async (formData: DonationListingData) => {
    // Check if user is authenticated before creating listing
    if (!isAuthenticated) {
      console.error('User not authenticated')
      return
    }

    try {
      setIsSubmittingListing(true)
      let imageUrl = formData.imageUrl
      if (formData.photoFile) {
        imageUrl = await uploadDonationPhoto(formData.photoFile)
      }
      const { photoFile: _photoFile, ...rest } = formData
      await createDonation({ ...rest, imageUrl })
      setShowListingForm(false)
      // Refresh donations list
      const donations = await getMyCreatedDonations()
      setMyDeliveries(donations.slice(0, 5))
    } catch (error: any) {
      console.error('Error creating donation listing:', error)
      alert(error?.message || 'Failed to create donation. Please try again.')
    } finally {
      setIsSubmittingListing(false)
    }
  }

  const handleConfirmPickup = async (donationId: string) => {
    try {
      setActionDonationId(donationId)
      await markDonationPickedUp(donationId)
      const donations = await getMyCreatedDonations()
      setMyDeliveries(donations.slice(0, 5))
    } catch (error) {
      console.error('Error confirming pickup:', error)
      alert('Failed to confirm pickup. Please try again.')
    } finally {
      setActionDonationId(null)
    }
  }

  const stats = [
    {
      title: 'Active Listings',
      value: myDeliveries.filter(d => d.status === 'NEW').length.toString(),
      change: 'Active',
      trend: 'up',
      icon: Truck,
      color: 'text-secondary-600'
    }
  ]

  const urgencyColors = {
    LOW: 'bg-green-100 text-green-800',
    MEDIUM: 'bg-yellow-100 text-yellow-800',
    HIGH: 'bg-orange-100 text-orange-800',
    URGENT: 'bg-red-100 text-red-800'
  }

  const statusColors = {
    NEW: 'bg-blue-100 text-blue-800',
    ACCEPTED: 'bg-purple-100 text-purple-800',
    PICKED_UP: 'bg-yellow-100 text-yellow-800',
    DELIVERED: 'bg-green-100 text-green-800',
    CANCELLED: 'bg-gray-100 text-gray-800',
    EXPIRED: 'bg-red-100 text-red-800'
  }

  const formatStatus = (status: string): string => {
    return status
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ')
  }

  const canCreate =
    !!isAuthenticated &&
    user?.userType === 'SUPPLIER' &&
    !!user?.isVerified &&
    !!user?.emailVerified

  const openListingForm = () => {
    if (!canCreate) return
    setShowListingForm(true)
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Food Decorations */}
      <div className="fixed inset-0 pointer-events-none opacity-5 z-0">
        <Apple className="absolute top-16 left-8 h-10 w-10 text-red-500 animate-pulse" />
        <Coffee className="absolute top-32 right-12 h-8 w-8 text-amber-600 animate-bounce" />
        <Cake className="absolute bottom-32 left-16 h-12 w-12 text-orange-400 animate-pulse" />
        <Gift className="absolute bottom-16 right-24 h-6 w-6 text-green-500 animate-bounce" />
        <Apple className="absolute top-48 left-1/3 h-4 w-4 text-red-400 animate-pulse" />
        <Coffee className="absolute bottom-48 right-1/3 h-6 w-6 text-amber-500 animate-bounce" />
      </div>

      {/* Compact Header */}
      <div className="bg-gradient-to-r from-secondary-50 to-primary-50 border-b border-gray-200 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
            <div className="flex items-center">
              <div className="relative mr-3">
                <Cake className="h-8 w-8 text-secondary-600" />
                <Apple className="absolute -top-1 -right-1 h-3 w-3 text-red-500" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center">
                  Welcome back, {user?.firstName}!
                  <Gift className="ml-2 h-5 w-5 text-green-500" />
                </h1>
                <p className="text-sm text-gray-600">
                  Ready to make a difference? Here's your donation dashboard
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={openListingForm}
                className={`btn-primary inline-flex items-center ${!canCreate ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={!canCreate}
                title={
                  canCreate
                    ? 'Create donation'
                    : 'Available after admin approval'
                }
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Donation
              </button>
              <Link
                to="/supplier/archived"
                className="btn-outline inline-flex items-center"
              >
                <Package className="h-4 w-4 mr-2" />
                Archived
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 relative z-10">
        {!canCreate && user?.userType === 'SUPPLIER' && (
          <div className="mb-6 bg-amber-50 border border-amber-300 rounded-lg p-4">
            <p className="text-sm font-semibold text-amber-950">
              {user?.emailVerified && !user?.isVerified
                ? 'Waiting for admin approval — you cannot create donations yet'
                : 'Account setup required before you can create donations'}
            </p>
            <p className="text-sm text-amber-900 mt-2">
              Suppliers <strong>cannot create donations until an admin approves your account</strong>.
              {!user?.emailVerified
                ? ' Verify your email, submit business verification, then wait for approval.'
                : businessVerification?.status !== 'PENDING' && businessVerification?.status !== 'APPROVED'
                  ? ' Submit business verification, then wait for approval.'
                  : ' Waiting for an admin to approve your account.'}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {!user?.emailVerified && (
                <Link to="/verify-email" className="btn-outline text-sm">
                  Verify email
                </Link>
              )}
              {businessVerification?.status !== 'PENDING' &&
                businessVerification?.status !== 'APPROVED' && (
                <Link to="/business-verification" className="btn-outline text-sm">
                  Business verification
                </Link>
              )}
              <Link to="/profile" className="btn-outline text-sm">
                Add directory photo
              </Link>
              <span className="text-xs text-amber-800 self-center">
                Business verification status: {businessVerification?.status || 'not submitted'}
                {!user?.isVerified ? ' • Admin approval required' : ''}
              </span>
            </div>
          </div>
        )}

        {/* Hero Section with Image */}
        <div className="bg-gradient-to-r from-secondary-50 to-primary-50 rounded-xl p-6 mb-6 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img 
              src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" 
              alt="Food donation" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 flex flex-col lg:flex-row items-center gap-6">
            <div className="flex-1 text-center lg:text-left">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Impact Matters</h2>
              <p className="text-gray-600 mb-4">
                Every donation helps reduce food waste and feeds families in need. 
                Thank you for being part of our community!
              </p>
              {/* Removed hardcoded impact stats */}
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" 
                alt="Fresh produce" 
                className="w-48 h-32 object-cover rounded-lg shadow-lg"
              />
              <div className="absolute -top-2 -right-2 bg-white rounded-full p-2 shadow-lg">
                <Gift className="h-5 w-5 text-green-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm p-4 border border-gray-200 relative overflow-hidden">
              {/* Food decoration for each stat */}
              {index === 0 && <Apple className="absolute top-1 right-1 h-4 w-4 text-red-400 opacity-30" />}
              {index === 1 && <Coffee className="absolute top-1 right-1 h-4 w-4 text-amber-400 opacity-30" />}
              {index === 2 && <Cake className="absolute top-1 right-1 h-4 w-4 text-orange-400 opacity-30" />}
              {index === 3 && <Gift className="absolute top-1 right-1 h-4 w-4 text-green-400 opacity-30" />}
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-600">{stat.title}</p>
                  <p className="text-xl font-bold text-gray-900 mt-1">{stat.value}</p>
                  <p className="text-xs text-green-600 mt-1">
                    {stat.change} from last month
                  </p>
                </div>
                <div className={`p-2 rounded-full bg-gray-50 ${stat.color}`}>
                  <stat.icon className="h-5 w-5" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* My Donation Listings */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-900">
                    My Donation Listings
                  </h2>
                  <Link to="/my-deliveries" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                    View all
                  </Link>
                </div>
              </div>
              <div className="p-6">
                {isLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="text-gray-500 mt-2">Loading your donations...</p>
                  </div>
                ) : myDeliveries.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">No donations posted yet</p>
                    <button 
                      onClick={openListingForm}
                      className={`btn-primary ${!canCreate ? 'opacity-50 cursor-not-allowed' : ''}`}
                      disabled={!canCreate}
                      title={
                        canCreate
                          ? 'Create donation'
                          : 'Available after admin approval'
                      }
                    >
                      Post Your First Donation
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {myDeliveries.map((delivery) => (
                      <div key={delivery.id} className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors">
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="font-medium text-gray-900">{delivery.title}</h3>
                          <div className="flex gap-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${urgencyColors[delivery.urgency as keyof typeof urgencyColors]}`}>
                              {delivery.urgency}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[delivery.status as keyof typeof statusColors]}`}>
                              {formatStatus(delivery.status)}
                            </span>
                          </div>
                        </div>
                        <p className="text-gray-600 text-sm mb-3">{delivery.description}</p>

                        {/* Workflow: accepted -> supplier confirms pickup -> food bank confirms delivery */}
                        {delivery.status === 'ACCEPTED' && delivery.volunteer && (
                          <div className="mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-purple-50 border border-purple-200 rounded-lg p-3">
                            <div className="text-sm text-purple-900">
                              <span className="font-medium">Claimed by: </span>
                              <Link
                                to={`/volunteers/${delivery.volunteer.id}`}
                                className="font-semibold text-primary-700 hover:underline"
                              >
                                {delivery.volunteer.firstName} {delivery.volunteer.lastName}
                              </Link>
                              {delivery.foodBank && (
                                <span className="text-purple-800">
                                  {' '}
                                  (Delivering to: {delivery.foodBank.firstName} {delivery.foodBank.lastName})
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => handleConfirmPickup(delivery.id)}
                              disabled={actionDonationId === delivery.id}
                              className={`btn-primary ${actionDonationId === delivery.id ? 'opacity-60 cursor-not-allowed' : ''}`}
                            >
                              {actionDonationId === delivery.id ? 'Confirming...' : 'Confirm Pickup'}
                            </button>
                          </div>
                        )}

                        {delivery.status === 'PICKED_UP' && (
                          <div className="mb-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-900">
                            ✅ Pickup confirmed. Waiting for food bank delivery confirmation.
                          </div>
                        )}

                        {delivery.status === 'DELIVERED' && (
                          <div className="mb-3 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-900">
                            🎉 Completed — pickup and delivery confirmed.
                          </div>
                        )}

                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <div className="flex items-center gap-4">
                            <span className="flex items-center">
                              <MapPin className="h-4 w-4 mr-1" />
                              {delivery.pickupAddress}
                            </span>
                            <span className="flex items-center">
                              <Package className="h-4 w-4 mr-1" />
                              {delivery.estimatedWeight}
                            </span>
                          </div>
                          <Link 
                            to={`/delivery/${delivery.id}`}
                            className="text-primary-600 hover:text-primary-700 font-medium"
                          >
                            Manage
                          </Link>
                        </div>
                      </div>
                    ))}
                    {/* Post Donation button when there are existing donations */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <button 
                        onClick={() => setShowListingForm(true)}
                        className="btn-primary w-full"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Post Donation
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions & Impact */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={openListingForm}
                  className={`flex items-center p-3 rounded-lg border border-gray-200 w-full text-left transition-colors ${
                    canCreate
                      ? 'hover:border-primary-300 hover:bg-primary-50'
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                  disabled={!canCreate}
                  title={
                    canCreate
                      ? 'Create donation'
                      : 'Available after admin approval'
                  }
                >
                  <Plus className="h-5 w-5 text-primary-600 mr-3" />
                  <span className="font-medium text-gray-900">Create Donation</span>
                </button>
                <Link
                  to="/food-banks"
                  className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-secondary-300 hover:bg-secondary-50 transition-colors"
                >
                  <MapPin className="h-5 w-5 text-secondary-600 mr-3" />
                  <span className="font-medium text-gray-900">Find Food Banks</span>
                </Link>
                <Link
                  to="/supplier/archived"
                  className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  <Package className="h-5 w-5 text-gray-700 mr-3" />
                  <span className="font-medium text-gray-900">Archived Deliveries</span>
                </Link>
              </div>
            </div>


          </div>
        </div>
      </div>

      {/* Donation Listing Form Modal */}
      {showListingForm && (
        <DonationListingForm
          onClose={() => setShowListingForm(false)}
          onSubmit={handleCreateListing}
          isSubmitting={isSubmittingListing}
          userType="SUPPLIER"
        />
      )}
    </div>
  )
}

export default SupplierDashboard